import React, { useContext, useState, useEffect, useRef } from "react";
import SectionTitle from "components/header/navigation/section/Title";
import Flyout from "components/header/navigation/Flyout";
import Loading from "components/shared/Loading";
import Full from "./Full";
import Empty from "./Empty";
import useFetch from "hooks/useFetch";
import useKeyboardNav from "hooks/useKeyboardNav";
import useSessionStorage from "hooks/useSessionStorage";
import { arrayIsEmpty, objectIsEmpty, mapString } from "utils";
import { getCookie, setCookie, removeCookie } from "utils/cookie";
import { Context } from "Context";
import config from "config.json";
const syndicatedMakeCartRequest = "syndicatedMakeUserCartRequest";
const cartUserIdentifier = "cartUserIdentifier";
const cartIdName = "cartId";
const cartEvent = "navigationCart";
const flyoutPositionLeft = "left";

const Cart = ({
	cartEmpty,
	cartLabel,
	checkOut,
	descriptions = [],
	icon,
	id,
	link,
	price: initialPrice,
	standaloneItemsTitle: otherItems,
	title,
	viewCartCheckOut,
	viewDetails,
	startOver,
	emptyCart,
	accessibiltyText,
	isMenuActive,
	setIsMenuActive,
	cartStore = "",
	flyoutPosition = flyoutPositionLeft,
	additionalItems: additionalItemsText,
	isMobileView,
	isLastSection,
}) => {
	const {
		config: { brand, store: initialStoreValue },
		profileData: { user },
		cartHasItems: hasItems,
		setCartHasItems: setHasItems,
	} = useContext(Context);

	const [storageMakeCartRequest, setStorageMakeCartRequest] = useSessionStorage(
		syndicatedMakeCartRequest
	);

	const brandConfig = config.brands?.[brand] || {};
	const store = initialStoreValue || cartStore;
	const isDCL = brand === "dcl";
	const showFlyout =
		isDCL || !window?.location.pathname.split("/").includes("cart");
	const [cartTimestamp, setCartTimestamp] = useState(new Date());
	const [detailsUrl, setDetailsUrl] = useState("");
	const [scope] = useState(
		isDCL ? "" : `${brandConfig.cartScope || brand}${store ? `_${store}` : ""}`
	);
	const [items, setItems] = useState([]);
	const [standaloneItems, setStandaloneItems] = useState([]);
	const [compositeItems, setCompositeItems] = useState([]);
	const [standaloneItemsTitle, setStandaloneItemsTitle] = useState("");
	const [price, setPrice] = useState(initialPrice);
	const [unavailableItemsMessage, setUnavailableItemsMessage] = useState({});
	const [currencyChangeDetail, setCurrencyChangeDetail] = useState(null);
	const [cartCookieJarName, setCartCookieJarName] = useState("");
	const [cartCookieJarValue, setCartCookieJarValue] = useState({});
	const [checkoutRefactorValue, setcheckoutRefactorValue] = useState(false);
	const [onHover, setOnHover] = useState(false);
	const sectionRef = useRef(null);
	const { onFocus, handleKeyDown, handleOnFocus, handleOnBlur } =
		useKeyboardNav(sectionRef, isMenuActive, setIsMenuActive, isLastSection);

	const mainClass = "syndicated-section";
	let classes = `${mainClass} ${mainClass}--${brand}`;
	if (id) classes += ` ${mainClass}--${id}`;
	classes += ` ${mainClass}--${hasItems ? "full" : "empty"}`;
	if (!showFlyout) classes += ` ${mainClass}--no-flyout`;
	if (isMenuActive && onFocus) classes += ` ${mainClass}--onfocus`;
	if (onHover) classes += ` ${mainClass}--onhover`;

	const isTradePage = () => {
		return store && store !== cartStore;
	};

	const isUserLoggedIn = () => {
		return !!user?.swid;
	};

	const getHeaders = () => {
		return {
			headers: {
				swid: isTradePage() ? "" : user?.swid || "",
				token: user?.authToken || "",
			},
		};
	};

	const { response, error, loading } = useFetch(
		detailsUrl,
		getHeaders(),
		0,
		detailsUrl
	);

	const getCartCookies = () => {
		const cartCookieName = isDCL
			? "new"
			: user?.swid
			? cartUserIdentifier
			: cartIdName;
		const cartCookieJarName = isDCL ? "cartIdMapping" : `CART-${scope}_jar`;

		setCartCookieJarName(cartCookieJarName);
		let cartCookieJarContent = getCookie(cartCookieJarName);
		const checkoutRefactor = getCookie(brandConfig?.checkoutRefactorCookieName);
		if (checkoutRefactor) {
			setcheckoutRefactorValue(true);
		}
		if (cartCookieJarContent) {
			try {
				cartCookieJarContent = JSON.parse(
					decodeURIComponent(cartCookieJarContent)
				);
				if (
					cartCookieJarContent[cartCookieName] ||
					(user?.swid && cartCookieJarContent[cartIdName])
				) {
					setCartCookieJarValue(cartCookieJarContent);
				}
			} catch (e) {
				resetCartCookie(cartCookieJarName);
				console.info("Error parsing the cart cookie, content is malformed");
			}
		}
	};

	const getTimeMachine = () => {
		let timeMachine = "";
		const timeMachineCookie = getCookie("setSystemTime");
		if (!timeMachineCookie) return "";
		const togglesCookie = getCookie("featureToggles");
		if (!togglesCookie) return "";
		try {
			const timeMachineToggle = JSON.parse(
				decodeURIComponent(togglesCookie)
			).systemTimeOverrideHeader;
			if (timeMachineToggle) {
				timeMachine = timeMachineCookie;
			}
		} catch (error) {
			console.error("Error parsing the featureToggles cookie");
		}
		return timeMachine;
	};

	const setRequestUrls = () => {
		const isLoggedIn = isUserLoggedIn();
		const {
			cartId = "",
			new: cartIdDcl = null,
			[cartUserIdentifier]: {
				id = null,
				type = !isDCL && isLoggedIn && !isTradePage() ? "GUEST" : "",
				idType = !isDCL && isLoggedIn && !isTradePage() ? "swid" : "",
			} = {},
		} = cartCookieJarValue;

		// if swid cookie matches the cartUserIdentifier swid cookie of the CART-XX_jar
		const isUserCartCookieSet =
			isLoggedIn && id && type && idType && id === user.swid;
		isUserCartCookieSet && setStorageMakeCartRequest(true);
		const makeRequest =
			isLoggedIn && !isUserCartCookieSet && storageMakeCartRequest;
		const isValidCartDCL = !!(isDCL && cartIdDcl);
		const isValidCartOtherBrands = !!(
			scope &&
			(cartId || isUserCartCookieSet || makeRequest)
		);
		const timeMachine = getTimeMachine();

		if (isValidCartDCL || isValidCartOtherBrands) {
			setDetailsUrl(
				mapString(
					config.cart.apiUrl,
					"details",
					scope,
					isDCL ? cartIdDcl : cartId,
					type,
					idType,
					timeMachine,
					checkoutRefactorValue
				)
			);
		}
	};

	const setUserCartCookie = () => {
		if (
			!brandConfig.preventSettingUserCartCookie &&
			user?.swid &&
			(objectIsEmpty(cartCookieJarValue) || cartCookieJarValue?.[cartIdName])
		) {
			const cookieContent = {
				[cartUserIdentifier]: {
					type: "GUEST",
					id: user?.swid,
					idType: "swid",
				},
			};
			setCookie(
				cartCookieJarName,
				encodeURIComponent(JSON.stringify(cookieContent)),
				config.cookieExpirationInDays
			);
			setCartCookieJarValue(cookieContent);
		}
	};

	const resetCartCookie = (cartCookieName = "") => {
		user?.swid && setStorageMakeCartRequest(false);
		setCartCookieJarValue({});
		if (!checkoutRefactorValue) {
			removeCookie(cartCookieJarName || cartCookieName);
		}
	};

	const resetCart = () => {
		setDetailsUrl("");
		setItems([]);
		setHasItems(false);
	};

	const cartEventLogic = (event) => {
		if (event?.detail?.method === "reload") {
			resetCart();
			setCartTimestamp(new Date());
		}
	};

	const validateCartStatus = (response) => {
		if (
			(response.status === 404 || response.status === 204) &&
			!isTradePage() &&
			!isUserLoggedIn()
		) {
			resetCartCookie();
		}
	};

	const handleMouseEnter = () => {
		setOnHover(true);
	};

	const handleMouseLeave = () => {
		setOnHover(false);
	};

	useEffect(() => {
		typeof storageMakeCartRequest === "undefined" &&
			setStorageMakeCartRequest(true);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		getCartCookies();
		if (!user?.swid && hasItems) {
			resetCart();
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.swid, store, cartTimestamp]);

	useEffect(() => {
		(cartCookieJarValue || user?.swid) && setRequestUrls();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cartCookieJarValue, storageMakeCartRequest, user?.swid, hasItems]);

	useEffect(() => {
		if (response) {
			if (response.totalNumberOfItems > 0 && !arrayIsEmpty(response.items)) {
				setHasItems(true);
				if (!isTradePage()) {
					setUserCartCookie();
				}
				setItems(response.items);
				!objectIsEmpty(response.price) && setPrice(response.price);
				!objectIsEmpty(response.message) &&
					setUnavailableItemsMessage(response.message);
				!objectIsEmpty(response.currencyChangeDetail) &&
					setCurrencyChangeDetail(response.currencyChangeDetail);
			}
			validateCartStatus(response);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		if (error) {
			validateCartStatus(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		setCompositeItems(items.filter((item) => !arrayIsEmpty(item?.items)));
		setStandaloneItems(items.filter((item) => !item.items));
	}, [items]);

	useEffect(() => {
		!arrayIsEmpty(standaloneItems) &&
			!arrayIsEmpty(compositeItems) &&
			setStandaloneItemsTitle(otherItems);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [standaloneItems, compositeItems]);

	useEffect(() => {
		document?.addEventListener(cartEvent, cartEventLogic);
		return () => {
			document?.removeEventListener(cartEvent, cartEventLogic);
		};
	});

	return (
		<div
			className={classes}
			tabIndex={isMobileView ? null : -1}
			ref={sectionRef}
			onKeyDown={handleKeyDown}
			onFocus={handleOnFocus}
			onBlur={handleOnBlur}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			role="group"
		>
			<SectionTitle
				link={link}
				name={id}
				icon={hasItems ? "cart-full" : icon}
				title={title}
				accessibiltyText={accessibiltyText}
				brand={brand}
				isMenuActive={isMenuActive && onFocus}
			/>
			{showFlyout && (
				<Flyout left={flyoutPosition === flyoutPositionLeft} name={id}>
					<Flyout.Primary name={id}>
						<div className={`${mainClass}__content`}>
							<h2 className={`${mainClass}__content__title`}>{cartLabel}</h2>
							{loading ? (
								<Loading />
							) : hasItems ? (
								<Full
									brand={brand}
									message={unavailableItemsMessage}
									compositeItems={compositeItems}
									standaloneItems={standaloneItems}
									standaloneItemsTitle={standaloneItemsTitle}
									price={price}
									currencyChangeDetail={currencyChangeDetail}
									startOver={startOver}
									emptyCart={emptyCart}
									buttons={{
										checkOut,
										viewCartCheckOut,
										viewDetails,
									}}
									totalNumberOfItems={response?.totalNumberOfItems}
									additionalItemsText={additionalItemsText}
								/>
							) : (
								<Empty
									id={id}
									brand={brand}
									text={cartEmpty}
									descriptions={descriptions || []}
									price={price?.sections || initialPrice?.sections || {}}
									separateFooter={isDCL}
								/>
							)}
						</div>
					</Flyout.Primary>
				</Flyout>
			)}
		</div>
	);
};

export default Cart;
